import { useEffect, useRef, useState } from 'react';
import type { DocumentFieldView, FieldType } from '@otter/shared';
import { api } from '../lib/api';
import { loadPdf, renderPageToCanvas, type LoadedPdf } from '../lib/pdfRender';

// Visual field designer: renders the source PDF and lets the admin place,
// move, resize and assign signature / date / text fields per signer. Geometry
// is kept as fractions [0,1] of the page (x,y from the top-left).

const DISPLAY_WIDTH = 620;

type SignerLite = { id: string; fullName: string };

type Field = {
  key: string;
  id?: string;
  signerId: string;
  type: FieldType;
  page: number;
  x: number;
  y: number;
  w: number;
  h: number;
};

const TYPE_LABEL: Record<FieldType, string> = {
  signature: 'Firma',
  date: 'Fecha',
  text: 'Texto',
};

const DEFAULTS: Record<FieldType, { w: number; h: number }> = {
  signature: { w: 0.26, h: 0.07 },
  date: { w: 0.16, h: 0.032 },
  text: { w: 0.26, h: 0.038 },
};

const clamp = (v: number, lo = 0, hi = 1) => Math.max(lo, Math.min(hi, v));

const SIGNER_COLORS = ['#ff5722', '#2563eb', '#16a34a', '#9333ea', '#db2777'];

export function FieldDesigner(props: {
  documentId: string;
  signers: SignerLite[];
  onClose: () => void;
  onSaved: () => void;
  onError: (e: unknown) => void;
}) {
  const { documentId, signers } = props;
  const [pdf, setPdf] = useState<LoadedPdf | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [fields, setFields] = useState<Field[]>([]);
  const [type, setType] = useState<FieldType>('signature');
  const [signerId, setSignerId] = useState(signers[0]?.id ?? '');
  const [busy, setBusy] = useState(false);

  const signerColor = (id: string) =>
    SIGNER_COLORS[Math.max(0, signers.findIndex((s) => s.id === id)) % SIGNER_COLORS.length] ??
    '#ff5722';

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const doc = await loadPdf(`/admin/documents/${documentId}/source`);
        if (cancelled) return;
        setPdf(doc);
        setNumPages(doc.numPages);
        const res = await api<{ fields: DocumentFieldView[] }>(
          `/admin/documents/${documentId}/fields`,
        );
        if (cancelled) return;
        setFields(
          res.fields.map((f) => ({
            key: f.id,
            id: f.id,
            signerId: f.signerId,
            type: f.type,
            page: f.page,
            x: f.x,
            y: f.y,
            w: f.w,
            h: f.h,
          })),
        );
      } catch (e) {
        props.onError(e);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  function addFieldAt(page: number, xf: number, yf: number) {
    if (!signerId) return;
    const def = DEFAULTS[type];
    setFields((list) => [
      ...list,
      {
        key: crypto.randomUUID(),
        signerId,
        type,
        page,
        x: clamp(xf - def.w / 2, 0, 1 - def.w),
        y: clamp(yf - def.h / 2, 0, 1 - def.h),
        w: def.w,
        h: def.h,
      },
    ]);
  }

  function updateField(key: string, patch: Partial<Field>) {
    setFields((list) => list.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  }

  function removeField(key: string) {
    setFields((list) => list.filter((f) => f.key !== key));
  }

  async function save() {
    setBusy(true);
    try {
      await api(`/admin/documents/${documentId}/fields`, {
        method: 'PUT',
        body: {
          fields: fields.map((f) => ({
            signerId: f.signerId,
            type: f.type,
            page: f.page,
            x: f.x,
            y: f.y,
            w: f.w,
            h: f.h,
            required: true,
          })),
        },
      });
      props.onSaved();
    } catch (e) {
      props.onError(e);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dz-overlay">
      <div className="dz-toolbar">
        <strong>Diseñar campos</strong>
        <div className="dz-tools">
          {(['signature', 'date', 'text'] as FieldType[]).map((t) => (
            <button
              key={t}
              className={`btn ${type === t ? 'primary' : ''}`}
              onClick={() => setType(t)}
            >
              {TYPE_LABEL[t]}
            </button>
          ))}
          <select value={signerId} onChange={(e) => setSignerId(e.target.value)}>
            {signers.map((s) => (
              <option key={s.id} value={s.id}>
                {s.fullName}
              </option>
            ))}
          </select>
          <span className="dz-hint">Haz clic en el documento para colocar el campo</span>
        </div>
        <div className="dz-legend">
          {signers.map((s) => (
            <span key={s.id} className="dz-legend-item">
              <span className="dz-legend-dot" style={{ background: signerColor(s.id) }} />
              {s.fullName}
            </span>
          ))}
        </div>
        <div className="dz-actions">
          <button className="btn" onClick={props.onClose} disabled={busy}>
            Cancelar
          </button>
          <button className="btn primary" onClick={() => void save()} disabled={busy}>
            {busy ? 'Guardando…' : 'Guardar campos'}
          </button>
        </div>
      </div>

      <div className="dz-pages">
        {pdf &&
          Array.from({ length: numPages }, (_, i) => (
            <DesignerPage
              key={i}
              pdf={pdf}
              pageIndex={i}
              fields={fields.filter((f) => f.page === i)}
              signerColor={signerColor}
              signerLabel={(id) => signers.find((s) => s.id === id)?.fullName ?? '?'}
              onAdd={(xf, yf) => addFieldAt(i, xf, yf)}
              onUpdate={updateField}
              onRemove={removeField}
            />
          ))}
        {!pdf && <div className="dz-loading">Cargando documento…</div>}
      </div>
    </div>
  );
}

function DesignerPage(props: {
  pdf: LoadedPdf;
  pageIndex: number;
  fields: Field[];
  signerColor: (id: string) => string;
  signerLabel: (id: string) => string;
  onAdd: (xf: number, yf: number) => void;
  onUpdate: (key: string, patch: Partial<Field>) => void;
  onRemove: (key: string) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (!canvasRef.current) return;
      const s = await renderPageToCanvas(props.pdf, props.pageIndex + 1, canvasRef.current, DISPLAY_WIDTH);
      if (!cancelled) setSize({ w: s.width, h: s.height });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.pdf, props.pageIndex]);

  function onClickAdd(e: React.MouseEvent) {
    if (!wrapRef.current || (e.target as HTMLElement).closest('.dz-field')) return;
    const rect = wrapRef.current.getBoundingClientRect();
    props.onAdd((e.clientX - rect.left) / rect.width, (e.clientY - rect.top) / rect.height);
  }

  return (
    <div
      className="dz-page"
      ref={wrapRef}
      style={{ width: DISPLAY_WIDTH, height: size?.h ?? DISPLAY_WIDTH * 1.4 }}
      onClick={onClickAdd}
    >
      <canvas ref={canvasRef} className="dz-canvas" />
      {props.fields.map((f) => (
        <FieldBox
          key={f.key}
          field={f}
          color={props.signerColor(f.signerId)}
          label={props.signerLabel(f.signerId)}
          pageW={size?.w ?? DISPLAY_WIDTH}
          pageH={size?.h ?? DISPLAY_WIDTH * 1.4}
          onUpdate={props.onUpdate}
          onRemove={props.onRemove}
        />
      ))}
    </div>
  );
}

function FieldBox(props: {
  field: Field;
  color: string;
  label: string;
  pageW: number;
  pageH: number;
  onUpdate: (key: string, patch: Partial<Field>) => void;
  onRemove: (key: string) => void;
}) {
  const { field: f, pageW, pageH } = props;

  function startMove(e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const ox = f.x;
    const oy = f.y;
    const move = (ev: PointerEvent) => {
      const nx = clamp(ox + (ev.clientX - sx) / pageW, 0, 1 - f.w);
      const ny = clamp(oy + (ev.clientY - sy) / pageH, 0, 1 - f.h);
      props.onUpdate(f.key, { x: nx, y: ny });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  function startResize(e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const sx = e.clientX;
    const sy = e.clientY;
    const ow = f.w;
    const oh = f.h;
    const move = (ev: PointerEvent) => {
      const nw = clamp(ow + (ev.clientX - sx) / pageW, 0.03, 1 - f.x);
      const nh = clamp(oh + (ev.clientY - sy) / pageH, 0.015, 1 - f.y);
      props.onUpdate(f.key, { w: nw, h: nh });
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  }

  return (
    <div
      className="dz-field"
      onPointerDown={startMove}
      style={{
        left: `${f.x * 100}%`,
        top: `${f.y * 100}%`,
        width: `${f.w * 100}%`,
        height: `${f.h * 100}%`,
        borderColor: props.color,
        background: `${props.color}1a`,
      }}
    >
      <span className="dz-field-tag" style={{ background: props.color }}>
        {TYPE_LABEL[f.type]} · {props.label}
      </span>
      <button
        className="dz-field-del"
        onPointerDown={(e) => {
          e.stopPropagation();
          props.onRemove(f.key);
        }}
      >
        ×
      </button>
      <span className="dz-field-resize" onPointerDown={startResize} />
    </div>
  );
}
